import React, { useState, useEffect, useCallback, useMemo } from "react";
import { fetchTournamentDashboard, fetchTournamentsList, deleteTournament } from "../api/tournamentControlApi";
import TournamentDashboardMetricsSection from "../components/control/sections/TournamentDashboardMetricsSection";
import TournamentCreateSection from "../components/control/sections/TournamentCreateSection";
import TournamentDirectorySection, {
  TOURNAMENT_DIRECTORY_COLUMN_DEFS,
  createInitialTournamentColumnVisibility,
  tournamentDirectoryCellValue,
  tournamentDefaultDirectoryFilters,
} from "../components/control/sections/TournamentDirectorySection";
import TournamentRegistrationsCertificatesApprovalsSection from "../components/control/sections/TournamentRegistrationsCertificatesApprovalsSection";
import TournamentResultApprovalsSection from "../components/control/sections/TournamentResultApprovalsSection";
import TournamentMastersSection from "../components/control/sections/TournamentMastersSection";
import TournamentQuickJumpSection from "../components/control/sections/TournamentQuickJumpSection";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import GlobalErrorFlashModal from "../../../global/GlobalErrorFlashModal";
import { GlobalModal } from "../../../global/GlobalModal";
import { GlobalButton } from "../../../global/forms/GlobalFormFields";
import { useLiveDashboardStats } from "../../../global/hooks/useLiveDashboardStats";
import { getCsrfToken } from "../../../public/api/authApi";

import { getJwtRoleSlug } from "../../../public/utils/jwtRole";
import "../../user-builder/styles/userBuilder.css";
import "../../user-builder/styles/UserBuilderPage.css";

function safeErrorText(err, fallback = "Internal server error. Please try again later.") {
  const m = err?.message;
  return typeof m === "string" && m.trim() ? m : fallback;
}

const DIRECTORY_PAGE_SIZE = 10;

const TournamentControlPage = ({ showMasters: showMastersProp }) => {
  const roleSlug = getJwtRoleSlug(
    typeof localStorage !== "undefined" ? localStorage.getItem("jwt_token") : null,
  );
  const showMasters =
    showMastersProp !== undefined ? showMastersProp : roleSlug !== "sub-admin";
  const [statsReloadToken, setStatsReloadToken] = useState(0);
  const [tournaments, setTournaments] = useState([]);
  const [loadingTournaments, setLoadingTournaments] = useState(true);
  const [draftDirectoryFilters, setDraftDirectoryFilters] = useState(tournamentDefaultDirectoryFilters);
  const [appliedDirectoryFilters, setAppliedDirectoryFilters] = useState(tournamentDefaultDirectoryFilters);
  const [directoryPage, setDirectoryPage] = useState(1);
  const [directoryTotal, setDirectoryTotal] = useState(0);
  const [directoryTotalPages, setDirectoryTotalPages] = useState(1);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [activeQuickJump, setActiveQuickJump] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [tournamentToDelete, setTournamentToDelete] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [lookupsVersion, setLookupsVersion] = useState(0);
  const [editingTournament, setEditingTournament] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState(createInitialTournamentColumnVisibility);

  const bumpMasterLookups = useCallback(() => {
    setLookupsVersion((v) => v + 1);
  }, []);

  const { dashboardStats, loadingStats, refreshStats } = useLiveDashboardStats({
    fetcher: fetchTournamentDashboard,
    selectData: (data) => data?.data ?? null,
    refreshDeps: [statsReloadToken],
  });

  const fetchTournamentsListCb = useCallback(async () => {
    setLoadingTournaments(true);
    setErrorMsg("");
    try {
      const data = await fetchTournamentsList({ name: appliedDirectoryFilters.search });
      setTournaments(data?.data || []);
    } catch (err) {
      setErrorMsg(safeErrorText(err));
    } finally {
      setLoadingTournaments(false);
    }
  }, [appliedDirectoryFilters.search]);

  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedDirectoryFilters({ ...draftDirectoryFilters });
    }, 350);
    return () => clearTimeout(t);
  }, [draftDirectoryFilters]);

  useEffect(() => {
    const t = setTimeout(() => {
      fetchTournamentsListCb();
    }, 400);
    return () => clearTimeout(t);
  }, [appliedDirectoryFilters.search, fetchTournamentsListCb]);

  useEffect(() => {
    setDirectoryPage(1);
  }, [appliedDirectoryFilters]);

  const filteredDirectoryRows = useMemo(() => {
    const search = String(appliedDirectoryFilters.search || "").trim().toLowerCase();
    const col = appliedDirectoryFilters.column;
    const val = String(appliedDirectoryFilters.value || "").trim().toLowerCase();
    let list = [...tournaments];
    if (search) {
      list = list.filter((row) => {
        const code = String(row.tournament_code || "").toLowerCase();
        const name = String(row.tournament_name || "").toLowerCase();
        return code.includes(search) || name.includes(search);
      });
    }
    if (col && val) {
      list = list.filter((row) => String(row[col] ?? "").trim().toLowerCase() === val);
    }
    return list;
  }, [tournaments, appliedDirectoryFilters]);

  const pagedDirectoryRows = useMemo(() => {
    const total = filteredDirectoryRows.length;
    const pages = Math.max(1, Math.ceil(total / DIRECTORY_PAGE_SIZE));
    const safePage = Math.min(directoryPage, pages);
    const start = (safePage - 1) * DIRECTORY_PAGE_SIZE;
    const end = start + DIRECTORY_PAGE_SIZE;
    return filteredDirectoryRows.slice(start, end);
  }, [filteredDirectoryRows, directoryPage]);

  useEffect(() => {
    const total = filteredDirectoryRows.length;
    const pages = Math.max(1, Math.ceil(total / DIRECTORY_PAGE_SIZE));
    if (directoryPage > pages) setDirectoryPage(pages);
    setDirectoryTotal(total);
    setDirectoryTotalPages(pages);
  }, [filteredDirectoryRows, directoryPage]);

  const getTournamentExportData = useCallback(() => {
    const visibleCols = TOURNAMENT_DIRECTORY_COLUMN_DEFS.filter((c) => columnVisibility[c.id]);
    const headers = visibleCols.map((c) => c.label);
    const rows = filteredDirectoryRows.map((t) =>
      visibleCols.map((c) => String(tournamentDirectoryCellValue(c.id, t) ?? "")),
    );
    return { headers, rows };
  }, [filteredDirectoryRows, columnVisibility]);

  const handleFormSuccess = (msg) => {
    setSuccessMsg(msg);
    setErrorMsg("");
    setEditingTournament(null);
    setStatsReloadToken((t) => t + 1);
    refreshStats();
    fetchTournamentsListCb();
  };

  const handleRequestEdit = (row) => {
    setEditingTournament(row);
    requestAnimationFrame(() => {
      document.getElementById("td-create")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const handleDelete = (id) => {
    const row = tournaments.find((x) => x.id === id);
    setTournamentToDelete(row || { id, tournament_name: "this tournament" });
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!tournamentToDelete?.id) return;
    setIsConfirming(true);
    setErrorMsg("");
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      await deleteTournament(tournamentToDelete.id, csrfToken);
      setSuccessMsg("Tournament deactivated successfully.");
      setShowConfirmModal(false);
      setTournamentToDelete(null);
      if (editingTournament?.id === tournamentToDelete.id) setEditingTournament(null);
      setStatsReloadToken((t) => t + 1);
    refreshStats();
      fetchTournamentsListCb();
    } catch (err) {
      setErrorMsg(safeErrorText(err));
    } finally {
      setIsConfirming(false);
    }
  };

  const QUICK_JUMP_ITEMS = [
    { key: "stats", label: "Performance overview", targetId: "td-stats" },
    { key: "create", label: "Add tournament", targetId: "td-create" },
    { key: "list", label: "Directory", targetId: "td-list" },
    { key: "approvals_regs", label: "Registration approvals", targetId: "td-approvals-regs" },
    { key: "approvals_results", label: "Result approvals", targetId: "td-approvals-results" },
    { key: "masters", label: "Master tables", targetId: "td-masters" },
  ];

  return (
    <div className="user-builder-shell">
      <header className="user-builder-hero">
        <div className="user-builder-hero__text">
          <p className="user-builder-hero__eyebrow">Administrative Area</p>
          <h2 className="user-builder-hero__title">Tournaments Control</h2>
          <p className="user-builder-hero__sub">
            Create tournaments, track participation and approved revenue, approve registrations and results,
            manage sports, levels, update labels, and result categories.
          </p>
        </div>
      </header>

      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />
      <GlobalErrorFlashModal message={errorMsg} onDismiss={() => setErrorMsg("")} />

      <div className="user-builder-layout">
        <div className="user-builder-layout__main">
          <TournamentDashboardMetricsSection dashboardStats={dashboardStats} loadingStats={loadingStats} />

          <TournamentCreateSection
            lookupsRefreshKey={lookupsVersion}
            editingTournament={editingTournament}
            onCancelEdit={() => setEditingTournament(null)}
            onSuccess={handleFormSuccess}
          />

          <TournamentDirectorySection
            tournaments={tournaments}
            draftDirectoryFilters={draftDirectoryFilters}
            onDraftDirectoryFiltersChange={setDraftDirectoryFilters}
            onClearDirectoryFilters={() => setDraftDirectoryFilters(tournamentDefaultDirectoryFilters())}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            getTournamentExportData={getTournamentExportData}
            exportFilenameBase="admin_tournaments_directory"
            loadingTournaments={loadingTournaments}
            directoryRows={pagedDirectoryRows}
            directoryPage={directoryPage}
            directoryTotalPages={directoryTotalPages}
            directoryTotal={directoryTotal}
            directoryPageSize={DIRECTORY_PAGE_SIZE}
            onDirectoryPageChange={setDirectoryPage}
            onDelete={handleDelete}
            onRequestEdit={handleRequestEdit}
          />

          <TournamentRegistrationsCertificatesApprovalsSection />
          <TournamentResultApprovalsSection />

          {showMasters ? (
            <TournamentMastersSection onDirectoryChange={bumpMasterLookups} />
          ) : null}
        </div>

        <TournamentQuickJumpSection
          asideAriaLabel="Tournament sections"
          items={QUICK_JUMP_ITEMS}
          activeKey={activeQuickJump}
          onActiveChange={setActiveQuickJump}
        />
      </div>

      <GlobalModal
        open={showConfirmModal}
        onClose={() => !isConfirming && setShowConfirmModal(false)}
        title="Deactivate tournament"
        footer={
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              justifyContent: "flex-end",
            }}
          >
            <GlobalButton onClick={() => setShowConfirmModal(false)} disabled={isConfirming}>
              Cancel
            </GlobalButton>
            <GlobalButton variant="danger" onClick={handleConfirmDelete} loading={isConfirming}>
              Confirm
            </GlobalButton>
          </div>
        }
      >
        <p>
          Deactivate <b>{tournamentToDelete?.tournament_name}</b>? It will be hidden from this list but
          history stays in the database.
        </p>
      </GlobalModal>
    </div>
  );
};

export default TournamentControlPage;
